export type GSplatMaterialTemplateEntry = readonly [string, unknown]
export type GSplatMaterialTemplateChunkEntry = readonly [string, string]

export interface GSplatMaterialTemplateSnapshot {
  readonly revision: number
  readonly codeRevision: number
  readonly uniformRevision: number
  readonly defines: readonly GSplatMaterialTemplateEntry[]
  readonly parameters: readonly GSplatMaterialTemplateEntry[]
  readonly chunks: readonly GSplatMaterialTemplateChunkEntry[]
}

function freezeEntries<T>(source: ReadonlyMap<string, T>): readonly (readonly [string, T])[] {
  return Object.freeze(
    [...source.entries()].map(([name, value]) => Object.freeze([name, value] as const))
  )
}

function mapsEqual(a: ReadonlyMap<string, unknown>, b: ReadonlyMap<string, unknown>): boolean {
  if (a.size !== b.size) {
    return false
  }
  for (const [name, value] of a) {
    if (!b.has(name) || !Object.is(b.get(name), value)) {
      return false
    }
  }
  return true
}

/**
 * backend-neutral 的 GSplat material 逻辑模板。
 *
 * 该对象只保存 defines、parameters 与 shader chunks，不持有 Three/WebGPU material。code
 * 与 uniform revision 分离，实际 View/Manager material 可在 Prepare 阶段按快照同步。
 */
export class GSplatMaterialTemplate {
  private readonly defines = new Map<string, unknown>()
  private readonly parameters = new Map<string, unknown>()
  private readonly chunks = new Map<string, string>()
  private revisionValue = 0
  private codeRevisionValue = 0
  private uniformRevisionValue = 0
  private disposed = false

  get revision(): number {
    return this.revisionValue
  }

  get codeRevision(): number {
    return this.codeRevisionValue
  }

  get uniformRevision(): number {
    return this.uniformRevisionValue
  }

  get isDisposed(): boolean {
    return this.disposed
  }

  getDefine(name: string): unknown {
    return this.defines.get(name)
  }

  getParameter(name: string): unknown {
    return this.parameters.get(name)
  }

  getChunk(name: string): string | undefined {
    return this.chunks.get(name)
  }

  setDefine(name: string, value: unknown): boolean {
    if (this.disposed || (Object.is(this.defines.get(name), value) && this.defines.has(name))) {
      return false
    }
    this.defines.set(name, value)
    this.commitCodeChange()
    return true
  }

  deleteDefine(name: string): boolean {
    if (this.disposed || !this.defines.has(name)) {
      return false
    }
    this.defines.delete(name)
    this.commitCodeChange()
    return true
  }

  setParameter(name: string, value: unknown): boolean {
    if (
      this.disposed ||
      (Object.is(this.parameters.get(name), value) && this.parameters.has(name))
    ) {
      return false
    }
    this.parameters.set(name, value)
    this.commitUniformChange()
    return true
  }

  deleteParameter(name: string): boolean {
    if (this.disposed || !this.parameters.has(name)) {
      return false
    }
    this.parameters.delete(name)
    this.commitUniformChange()
    return true
  }

  setChunk(name: string, source: string): boolean {
    if (this.disposed || (Object.is(this.chunks.get(name), source) && this.chunks.has(name))) {
      return false
    }
    this.chunks.set(name, source)
    this.commitCodeChange()
    return true
  }

  deleteChunk(name: string): boolean {
    if (this.disposed || !this.chunks.has(name)) {
      return false
    }
    this.chunks.delete(name)
    this.commitCodeChange()
    return true
  }

  /**
   * 原子替换全部逻辑状态。仅 code/define/chunk 变化时递增 codeRevision，仅参数变化时递增
   * uniformRevision；一次 replace 最多使总 revision 递增一次。
   */
  replace(snapshot: Partial<GSplatMaterialTemplateSnapshot>): boolean {
    if (this.disposed) {
      return false
    }

    const nextDefines = new Map(snapshot.defines ?? this.defines)
    const nextParameters = new Map(snapshot.parameters ?? this.parameters)
    const nextChunks = new Map(snapshot.chunks ?? this.chunks)
    const codeChanged = !mapsEqual(this.defines, nextDefines) || !mapsEqual(this.chunks, nextChunks)
    const uniformsChanged = !mapsEqual(this.parameters, nextParameters)

    if (!codeChanged && !uniformsChanged) {
      return false
    }

    this.defines.clear()
    for (const [name, value] of nextDefines) this.defines.set(name, value)
    this.parameters.clear()
    for (const [name, value] of nextParameters) this.parameters.set(name, value)
    this.chunks.clear()
    for (const [name, value] of nextChunks) this.chunks.set(name, value)

    this.revisionValue++
    if (codeChanged) this.codeRevisionValue++
    if (uniformsChanged) this.uniformRevisionValue++
    return true
  }

  clear(): boolean {
    return this.replace({
      defines: [],
      parameters: [],
      chunks: [],
    })
  }

  captureSnapshot(): GSplatMaterialTemplateSnapshot {
    return Object.freeze({
      revision: this.revisionValue,
      codeRevision: this.codeRevisionValue,
      uniformRevision: this.uniformRevisionValue,
      defines: freezeEntries(this.defines),
      parameters: freezeEntries(this.parameters),
      chunks: freezeEntries(this.chunks),
    })
  }

  /** 回滚 staged mutation 使用：恢复完整快照且不递增 revision。 */
  restoreSnapshot(snapshot: GSplatMaterialTemplateSnapshot): void {
    if (this.disposed) {
      return
    }
    this.revisionValue = snapshot.revision
    this.codeRevisionValue = snapshot.codeRevision
    this.uniformRevisionValue = snapshot.uniformRevision
    this.defines.clear()
    for (const [name, value] of snapshot.defines) this.defines.set(name, value)
    this.parameters.clear()
    for (const [name, value] of snapshot.parameters) this.parameters.set(name, value)
    this.chunks.clear()
    for (const [name, value] of snapshot.chunks) this.chunks.set(name, value)
  }

  dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    this.defines.clear()
    this.parameters.clear()
    this.chunks.clear()
  }

  private commitCodeChange(): void {
    this.revisionValue++
    this.codeRevisionValue++
  }

  private commitUniformChange(): void {
    this.revisionValue++
    this.uniformRevisionValue++
  }
}
